import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookCard } from "@/components/BookCard";
import { PostCard } from "@/components/PostCard";
import { PostComposer } from "@/components/PostComposer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, Plus, X, CalendarDays, Youtube, Headphones, Link2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ReadingListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { glossary, user } = useAuth();
  const [list, setList] = useState(null);
  const [posts, setPosts] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState("");
  const [itemOpen, setItemOpen] = useState(false);
  const [item, setItem] = useState({ tur: "etkinlik", baslik: "", url: "", tarih: "" });

  const load = useCallback(async () => {
    const [l, p] = await Promise.all([api.get(`/reading-lists/${id}`), api.get(`/reading-lists/${id}/posts`)]);
    setList(l.data); setPosts(p.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/books").then((r) => setAllBooks(r.data)).catch(() => {}); }, []);

  const deletePost = async (pid) => { await api.delete(`/posts/${pid}`); setPosts(posts.filter((x) => x.id !== pid)); };

  const canManage = list && (list.olusturan === user?.id || ["admin", "moderator"].includes(user?.role));
  const saveBooks = async (ids) => {
    try {
      await api.put(`/reading-lists/${id}`, { liste_adi: list.liste_adi, aciklama: list.aciklama, tur: list.tur, kitap_id_listesi: ids });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addBook = (bid) => {
    const ids = list.kitap_id_listesi || [];
    if (ids.includes(bid)) { toast("Bu kitap zaten listede."); return; }
    saveBooks([...ids, bid]); setQ(""); setSel(""); toast.success("Kitap listeye eklendi.");
  };
  const removeBook = (bid) => saveBooks((list.kitap_id_listesi || []).filter((x) => x !== bid));

  const addItem = async () => {
    if (!item.baslik.trim() || !item.url.trim()) { toast.error("Başlık ve bağlantı gerekli."); return; }
    try {
      await api.post(`/reading-lists/${id}/items`, { ...item, tarih: item.tarih ? new Date(item.tarih).toISOString() : null });
      setItemOpen(false); setItem({ tur: "etkinlik", baslik: "", url: "", tarih: "" });
      toast.success(item.tur === "etkinlik" ? "Etkinlik eklendi ve Etkinlikler sayfasında yayınlandı." : "Öğe eklendi."); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeItem = async (iid) => { await api.delete(`/reading-lists/${id}/items/${iid}`); load(); };
  const itemIcon = { etkinlik: CalendarDays, youtube: Youtube, podcast: Headphones, link: Link2 };

  if (!list) return <div className="max-w-6xl mx-auto px-6 py-12 text-muted-foreground">Yükleniyor...</div>;
  const results = q.trim().length >= 2 ? allBooks.filter((b) => `${b.baslik} ${b.yazar}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-terra mb-6"><ArrowLeft size={16} /> Geri</button>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">{list.tur}</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">{list.liste_adi}</h1>
      <p className="text-ink-2 mt-3 max-w-2xl leading-relaxed">{list.aciklama}</p>

      {canManage && (
        <div className="mt-6 border border-line rounded-xl p-4 bg-surface" data-testid="list-add-book">
          <p className="text-sm font-medium text-ink mb-2">Listeye Kitap Ekle</p>
          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
              <Input data-testid="list-book-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kitap adı/yazar yaz..." className="pl-9" />
              {results.length > 0 && (
                <div className="absolute top-11 left-0 right-0 bg-surface border border-line rounded-lg shadow-xl z-20 overflow-hidden" data-testid="list-book-results">
                  {results.map((b) => (
                    <button key={b.id} data-testid={`list-add-${b.id}`} onClick={() => addBook(b.id)} className="w-full text-left px-3 py-2 hover:bg-surface-alt text-ink text-sm">
                      📖 {b.baslik} <span className="text-muted-foreground">— {b.yazar}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 min-w-0 sm:w-[46%]">
              <select data-testid="list-book-select" value={sel} onChange={(e) => setSel(e.target.value)} className="flex-1 min-w-0 h-10 rounded-md border border-line bg-surface px-3 text-sm truncate">
                <option value="">Listeden seç...</option>
                {allBooks.map((b) => <option key={b.id} value={b.id}>{b.baslik.length > 40 ? b.baslik.slice(0, 40) + "…" : b.baslik} — {b.yazar}</option>)}
              </select>
              <Button data-testid="list-book-add-btn" disabled={!sel} onClick={() => addBook(sel)} className="bg-terra hover:bg-terra-hover text-white gap-1 h-10 shrink-0"><Plus size={15} /> Ekle</Button>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-serif text-2xl text-ink mt-8 mb-4">Kitaplar</h2>
      {list.kitaplar?.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-5">
          {list.kitaplar.map((b) => (
            <div key={b.id} className="relative group">
              <BookCard book={b} />
              {canManage && (
                <button onClick={() => removeBook(b.id)} data-testid={`list-remove-${b.id}`}
                  className="absolute top-2 right-2 bg-ink/80 text-page rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"><X size={14} /></button>
              )}
            </div>
          ))}
        </div>
      ) : <p className="text-muted-foreground text-sm">Bu listeye henüz kitap eklenmemiş.</p>}

      {/* Kulüp Materyalleri (etkinlik/video/podcast/bağlantı) */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-ink">Kulüp Materyalleri</h2>
          {canManage && (
            <Dialog open={itemOpen} onOpenChange={setItemOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-item-btn" className="bg-terra hover:bg-terra-hover text-white gap-1"><Plus size={15} /> Materyal Ekle</Button></DialogTrigger>
              <DialogContent className="bg-surface">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Materyal Ekle</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Tür</Label>
                    <Select value={item.tur} onValueChange={(v) => setItem({ ...item, tur: v })}>
                      <SelectTrigger className="mt-1" data-testid="item-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="etkinlik">Etkinlik</SelectItem>
                        <SelectItem value="youtube">YouTube Videosu</SelectItem>
                        <SelectItem value="podcast">Podcast</SelectItem>
                        <SelectItem value="link">Web Bağlantısı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Başlık</Label><Input data-testid="item-title" value={item.baslik} onChange={(e) => setItem({ ...item, baslik: e.target.value })} className="mt-1" /></div>
                  <div><Label>Bağlantı (URL)</Label><Input data-testid="item-url" value={item.url} onChange={(e) => setItem({ ...item, url: e.target.value })} className="mt-1" placeholder="https://..." /></div>
                  {item.tur === "etkinlik" && <div><Label>Tarih & Saat</Label><Input type="datetime-local" data-testid="item-date" value={item.tarih} onChange={(e) => setItem({ ...item, tarih: e.target.value })} className="mt-1" /></div>}
                </div>
                <DialogFooter><Button onClick={addItem} data-testid="item-save" className="bg-terra hover:bg-terra-hover text-white">Ekle</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {list.ekstra_ogeler?.length ? (
          <div className="space-y-2" data-testid="club-items">
            {list.ekstra_ogeler.map((it) => {
              const Icon = itemIcon[it.tur] || Link2;
              return (
                <div key={it.id} className="flex items-center gap-3 border border-line rounded-lg p-3 bg-surface">
                  <Icon size={18} className="text-terra shrink-0" />
                  <div className="flex-1 min-w-0">
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-ink hover:text-terra truncate flex items-center gap-1">{it.baslik} <ExternalLink size={12} /></a>
                    <p className="text-xs text-muted-foreground capitalize">{it.tur}{it.tarih ? ` · ${new Date(it.tarih).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}` : ""}</p>
                  </div>
                  {canManage && <button onClick={() => removeItem(it.id)} data-testid={`item-remove-${it.id}`} className="text-muted-foreground hover:text-destructive shrink-0"><X size={15} /></button>}
                </div>
              );
            })}
          </div>
        ) : <p className="text-muted-foreground text-sm">Henüz materyal eklenmemiş.</p>}
      </div>

      <div className="mt-12">
        <h2 className="font-serif text-2xl text-ink mb-4">Tartışma</h2>
        <PostComposer listId={id} onCreated={load} />
        <div className="space-y-4 mt-6" data-testid="list-posts">
          {posts.length === 0 && <p className="text-muted-foreground text-sm">İlk yorumu sen başlat!</p>}
          {posts.map((p) => <PostCard key={p.id} post={p} glossary={glossary} onDelete={deletePost} />)}
        </div>
      </div>
    </div>
  );
}
